/**
 * All Farm View Manager
 * API service for fetching All Farm View data including irrigation and performance metrics
 */

// Note: axios is expected to be configured globally with baseURL interceptor
// See app setup for axios configuration
var axios = require('axios');
var { error_message } = require('@managers/helpers');

var ALL_FARM_VIEW_PATH = 'all_farm_view';

/**
 * @typedef {Object} FieldAllFarmData
 * @property {string} fieldId
 * @property {string} fieldName
 * @property {number|null} weeklyPerformance - Weekly irrigation value
 * @property {string|null} weeklyPerformanceUnits - Display unit for weekly performance
 * @property {number|null} weeklyGoal - Weekly irrigation goal
 * @property {string|null} weeklyGoalUnits - Display unit for weekly goal
 * @property {boolean} isIrrigating - Whether field is currently irrigating
 * @property {string|null} lastIrrigationStartedAt - ISO timestamp of last irrigation start
 * @property {string|null} lastIrrigationEndedAt - ISO timestamp of last irrigation end
 * @property {'talgil'|'piezometer'|'manual'|'none'|null} lastIrrigationSource - Source of irrigation data
 * @property {string|null} lastTransmitterUpdate - ISO timestamp of last transmitter data
 */

/**
 * @typedef {Object} AllFarmViewDataResponse
 * @property {string} farmingUnitId
 * @property {FieldAllFarmData[]} fields
 * @property {string} units - Unit of measurement (e.g., "INCH", "MM")
 * @property {string} dataTimestamp - ISO timestamp of when data was generated
 */

/**
 * Transforms API response from snake_case to camelCase
 * @param {Object} apiResponse - Raw API response
 * @returns {AllFarmViewDataResponse|null}
 */
var transformResponse = function (apiResponse) {
  if (!apiResponse) {
    return null;
  }

  // Handle both direct response and wrapped response (with body)
  var data = apiResponse.body || apiResponse;

  // Guard against invalid data structure
  if (!data || typeof data !== 'object') {
    return null;
  }

  // Validate required fields
  if (!data.farming_unit_id) {
    console.error('Invalid API response: missing farming_unit_id');
    return null;
  }

  var fields = [];
  if (data.fields && Array.isArray(data.fields)) {
    fields = data.fields.map(function (field) {
      return {
        fieldId: field.field_id,
        fieldName: field.field_name,
        weeklyPerformance:
          field.weekly_performance !== undefined
            ? field.weekly_performance
            : null,
        weeklyPerformanceUnits: field.weekly_performance_units || null,
        weeklyGoal: field.weekly_goal !== undefined ? field.weekly_goal : null,
        weeklyGoalUnits: field.weekly_goal_units || null,
        isIrrigating: Boolean(field.is_irrigating),
        lastIrrigationStartedAt: field.last_irrigation_started_at || null,
        lastIrrigationEndedAt: field.last_irrigation_ended_at || null,
        lastIrrigationSource: field.last_irrigation_source || null,
        lastTransmitterUpdate: field.last_transmitter_update || null,
      };
    });
  }

  return {
    farmingUnitId: data.farming_unit_id,
    fields: fields,
    units: data.units || null,
    dataTimestamp: data.data_timestamp,
  };
};

/**
 * Fetches all farm view data for a farming unit from the network.
 * Real-time metrics are not persisted — callers show loading until this resolves.
 *
 * @param {string} farmingUnitId - The farming unit ID
 * @returns {Promise<AllFarmViewDataResponse|null>}
 */
var getAllFarmViewData = function (farmingUnitId) {
  if (!farmingUnitId || typeof farmingUnitId !== 'string') {
    return Promise.reject(
      new Error('Invalid farmingUnitId: must be a non-empty string')
    );
  }

  return axios
    .get(ALL_FARM_VIEW_PATH + '/' + farmingUnitId)
    .then(function (res) {
      return transformResponse(res.data);
    })
    .catch(function (e) {
      console.error(error_message(e));
      throw e;
    });
};

module.exports = {
  getAllFarmViewData: getAllFarmViewData,
};
